/**
 * Client-side slash commands (desktop registry parity, e15/task-3).
 *
 * Available client commands execute a real action. Removed names fall through
 * for authorized custom skills or ordinary text. Required features still being
 * built return an unavailable notice and cannot impersonate a specialist run.
 * Server/runtime commands fall through to their actual execution boundary.
 */

import { SLASH_COMMANDS, canonicalSlashName, parseReasoningCommandArgs, slashCommandStatus, supportedSlashCommands } from "@dayvise/client-sdk";
import type { SlashClient } from "@dayvise/client-sdk";

export type ClientAction =
  | { readonly kind: "compose-prompt"; readonly seed: string }
  | { readonly kind: "navigate"; readonly to: string }
  | { readonly kind: "agents-modal" }
  | { readonly kind: "copy-last-message" }
  | { readonly kind: "paste-into-composer" }
  | { readonly kind: "new-conversation" }
  | { readonly kind: "decide-approval"; readonly decision: "approved" | "rejected" }
  | { readonly kind: "steer"; readonly instruction: string }
  | { readonly kind: "queue"; readonly instruction: string }
  /** A prose verb carrying text: send the remainder as a normal message. */
  | { readonly kind: "send"; readonly text: string }
  | { readonly kind: "sign-out" }
  | { readonly kind: "reload-page" }
  | { readonly kind: "refresh-data" }
  | { readonly kind: "reasoning-display"; readonly show: boolean }
  | { readonly kind: "open-diff" }
  | { readonly kind: "open-rollback" }
  | { readonly kind: "open-worktrees" }
  | { readonly kind: "notice"; readonly note: string };

export interface ClientSlashCommand {
  readonly name: string;
  readonly args: string;
  readonly action: ClientAction;
}

const NAVIGATE: Readonly<Record<string, string>> = {
  cron: "/schedules",
  kanban: "/boards",
  insights: "/usage",
  plugins: "/settings/tools",
  subscription: "/billing",
  toolsets: "/settings/tools",
  upgrade: "/billing",
  snapshot: "/settings/snapshots",
  "memory-graph": "/settings/snapshots",
  bundles: "/settings/tools",
  blueprint: "/settings/tools",
  proactive: "/settings/proactive",
  yolo: "/settings/yolo",
  "codex-runtime": "/settings/runtime",
};

function actionFor(name: string, args: string): ClientAction {
  if (name === "agents") return { kind: "agents-modal" };
  if (name === "approve") return { kind: "decide-approval", decision: "approved" };
  if (name === "deny") return { kind: "decide-approval", decision: "rejected" };
  if (name === "clear" || name === "new") return { kind: "new-conversation" };
  if (name === "reload-mcp" || name === "reload-skills") return { kind: "refresh-data" };
  if (name === "diff") return { kind: "open-diff" };
  if (name === "rollback") return { kind: "open-rollback" };
  if (name === "worktree") return { kind: "open-worktrees" };
  if (name === "steer") return { kind: "steer", instruction: args };
  if (name === "queue") return { kind: "queue", instruction: args };
  const to = NAVIGATE[name];
  if (to !== undefined) return { kind: "navigate", to };
  return { kind: "notice", note: `/${name} is not implemented in this client.` };
}

/** Resolve a client-tier command, or null when the text is not one. */
export function answerClientSlash(text: string, client: SlashClient = "web"): ClientSlashCommand | null {
  const match = /^\/([A-Za-z0-9_-]+)(?:\s+([\s\S]*))?$/.exec(text.trim());
  if (!match) return null;
  const canonical = canonicalSlashName(match[1] ?? "");
  const rawArgs = (match[2] ?? "").trim();
  const args = canonical === "prompt" ? rawArgs : rawArgs.replace(/^[—–]\s*/, "").trim();
  const status = slashCommandStatus(canonical);
  // Removed names are free for authorized custom skills and ordinary text.
  if (status === "removed") return null;
  if (status === "planned") return { name: canonical, args,
    action: { kind: "notice", note: `/${canonical} is not available yet.` } };
  if (canonical === "reasoning") {
    const parsed = parseReasoningCommandArgs(args);
    if (parsed.kind === "display") return { name: canonical, args, action: { kind: "reasoning-display", show: parsed.show } };
  }
  // Server tier (/skills) and unknown names fall through to the send path.
  const meta = SLASH_COMMANDS.find(
    (command) => command.name === canonical && command.where === "client",
  );
  if (meta === undefined) return null;
  if (!supportedSlashCommands(client).some((command) => command.name === canonical)) {
    return { name: canonical, args, action: { kind: "notice", note: `/${canonical} is not available in this client.` } };
  }
  return { name: canonical, args, action: actionFor(canonical, args) };
}

/** The seams the composer plugs actions into (kept injectable to stay testable). */
export interface ClientActionContext {
  readonly composePrompt: (seed: string) => void;
  readonly navigateTo: (to: string) => void;
  readonly openAgents: () => void;
  readonly copyLastMessage: () => Promise<void>;
  readonly pasteFromClipboard: () => Promise<void>;
  readonly startNewConversation: () => void;
  readonly decideApproval: (decision: "approved" | "rejected") => Promise<void>;
  readonly steer: (instruction: string) => Promise<void>;
  readonly queue: (instruction: string) => void;
  readonly send: (text: string) => void;
  readonly signOut: () => void;
  readonly refreshData: () => void;
  readonly reloadPage: () => void;
  readonly notify: (note: string) => void;
  /** Focus the selected run's file differences (conversation-scoped). */
  readonly openDiff: () => void;
  /** Open the retained-revision rollback surface for this conversation. */
  readonly openRollback: () => void;
  /** Focus the conversation's worktree strip (CMD-110). */
  readonly openWorktrees: () => void;
  /** Returns whether the signed-in user's preference was persisted. */
  readonly setReasoningDisplay: (show: boolean) => boolean;
}

/** Execute a resolved command against the composer's context. */
export async function executeClientAction(
  command: ClientSlashCommand,
  context: ClientActionContext,
): Promise<void> {
  const action = command.action;
  switch (action.kind) {
    case "compose-prompt":
      return context.composePrompt(action.seed);
    case "reasoning-display": {
      const saved = context.setReasoningDisplay(action.show);
      return context.notify(`Reasoning ${action.show ? "shown" : "hidden"} in this client.${saved ? " Preference saved for this user and workspace." : " The display changed for this view, but the preference could not be saved."} Effort is unchanged.`);
    }
    case "navigate":
      return context.navigateTo(action.to);
    case "agents-modal":
      return context.openAgents();
    case "copy-last-message":
      return context.copyLastMessage();
    case "paste-into-composer":
      return context.pasteFromClipboard();
    case "new-conversation":
      return context.startNewConversation();
    case "decide-approval":
      return context.decideApproval(action.decision);
    case "steer":
      return context.steer(action.instruction);
    case "queue":
      return context.queue(action.instruction);
    case "send":
      return context.send(action.text);
    case "sign-out":
      return context.signOut();
    case "reload-page":
      return context.reloadPage();
    case "refresh-data":
      return context.refreshData();
    case "open-diff":
      return context.openDiff();
    case "open-rollback":
      return context.openRollback();
    case "open-worktrees":
      return context.openWorktrees();
    case "notice":
      return context.notify(action.note);
  }
}
